import React, { FC } from 'react';
import { WizardFormData } from '../types';
import ModuleEntity from '../../../../../../../../domain/entities/ModuleEntity';
import ButtonComponent from '../../../../../../../components/button/ButtonComponent';
import { ButtonType } from '../../../../../../../components/button/ButtonComponentProps';

export interface StepReviewProps {
    formData: WizardFormData;
    roleName: string;
    buName: string;
    positionName: string;
    bucketPathPreview: string[];
    permissionPreview: ModuleEntity[];
    isSubmitting: boolean;
    onSubmit: () => void;
}

/**
 * Step 5 — Review & Confirm.
 * Renders a read-only summary of all wizard form data and a Submit button.
 * The component re-renders with fresh props each time it mounts, reflecting
 * any edits made via back-navigation.
 */
export const Step5_Review: FC<StepReviewProps> = ({
    formData,
    roleName,
    buName,
    positionName,
    bucketPathPreview,
    permissionPreview,
    isSubmitting,
    onSubmit,
}) => {
    const fullName = `${formData.firstname} ${formData.lastname}`;

    return (
        <div className="wizard-step wizard-step-review">
            <h3 className="user_list_title">Review &amp; Confirm</h3>

            <div className="wizard-review-summary">
                <div className="wizard-review-field">
                    <span className="wizard-review-label">Email:</span>
                    <span className="wizard-review-value">{formData.email}</span>
                </div>

                <div className="wizard-review-field">
                    <span className="wizard-review-label">Full Name:</span>
                    <span className="wizard-review-value">{fullName}</span>
                </div>

                <div className="wizard-review-field">
                    <span className="wizard-review-label">Role:</span>
                    <span className="wizard-review-value">{roleName}</span>
                </div>

                <div className="wizard-review-field">
                    <span className="wizard-review-label">Business Unit:</span>
                    <span className="wizard-review-value">{buName}</span>
                </div>

                <div className="wizard-review-field">
                    <span className="wizard-review-label">Position:</span>
                    <span className="wizard-review-value">{positionName}</span>
                </div>

                <div className="wizard-review-field">
                    <span className="wizard-review-label">Timezone:</span>
                    <span className="wizard-review-value">{formData.timezone}</span>
                </div>

                {formData.supervisors.length > 0 && (
                    <div className="wizard-review-field">
                        <span className="wizard-review-label">Supervisor:</span>
                        <span className="wizard-review-value">{formData.supervisors.join(', ')}</span>
                    </div>
                )}

                <div className="wizard-review-field">
                    <span className="wizard-review-label">Bucket Paths:</span>
                    <span className="wizard-review-value">
                        {bucketPathPreview.length > 0 ? (
                            <ul className="wizard-review-bucket-list">
                                {bucketPathPreview.map((path, idx) => (
                                    <li key={idx}>{path}</li>
                                ))}
                            </ul>
                        ) : (
                            'N/A'
                        )}
                    </span>
                </div>

                {formData.bucketSelectionMode === 'tree' && formData.selectedBucketIds.length > 0 && (
                    <div className="wizard-review-field">
                        <span className="wizard-review-label">Selected Bucket IDs:</span>
                        <span className="wizard-review-value">
                            {formData.selectedBucketIds.join(', ')}
                        </span>
                    </div>
                )}
            </div>

            <div className="wizard-review-actions">
                <ButtonComponent
                    onClick={onSubmit}
                    text={isSubmitting ? 'Submitting...' : 'Submit'}
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    type={ButtonType.MAIN}
                />
            </div>
        </div>
    );
};

export default Step5_Review;
